'use client'

import type { QueryClient } from '@tanstack/react-query'

import { performLocalSessionCleanup } from '@/services/auth/logoutCleanup.client'
import { captureSessionEvent } from '@/services/auth/sessionTelemetry.client'

export type AnonymousSessionCleanupReason =
  | 'public_auth_cleanup'
  | 'refresh_token_revoked'
  | 'session_not_ready_after_already_authenticated'
  | 'session_not_ready_after_signin'
  | 'session_not_ready'
  | 'signed_out_public'
  | 'stale_public_state_cleared'
  | 'token_refresh_failure_public'
  | 'unexpected_sign_in_interruption'

export type UnavailableLoginSessionCleanupReason = Extract<
  AnonymousSessionCleanupReason,
  | 'session_not_ready_after_already_authenticated'
  | 'session_not_ready_after_signin'
  | 'unexpected_sign_in_interruption'
>

type ResetAnonymousSessionStateOptions = {
  skipSignOut?: boolean
  clearAppCookies?: boolean
  clearPersistPreference?: boolean
  captureTelemetry?: boolean
}

export async function resetAnonymousSessionState(
  queryClient: QueryClient,
  reason: AnonymousSessionCleanupReason,
  options?: ResetAnonymousSessionStateOptions,
): Promise<boolean> {
  const authArtifactsCleared = await performLocalSessionCleanup(queryClient, {
    skipSignOut: options?.skipSignOut,
    clearPersistPreference: options?.clearPersistPreference,
    clearAppCookies: options?.clearAppCookies ?? true,
    clearLegacyAuthState: true,
  })

  if (authArtifactsCleared && options?.captureTelemetry !== false) {
    captureSessionEvent('public_auth_cleanup', reason)
  }

  return authArtifactsCleared
}

export async function recoverUnavailableLoginSession(
  queryClient: QueryClient,
  reason: UnavailableLoginSessionCleanupReason,
): Promise<boolean> {
  return resetAnonymousSessionState(queryClient, reason, {
    skipSignOut: true,
    clearAppCookies: true,
    clearPersistPreference: true,
  })
}
